<!--
  BillingTermCard — the "Choose your billing term" picker (Variant B, approved
  2026-07-10). The single source for the plan picker on all three surfaces:
  the homepage (Pricing.vue), /membership, and the logged-in paywall.

  It was inlined twice before this component existed, and the two copies had
  already drifted apart on their fineprint. Anything that differs per surface is
  a prop; the card itself — the two rows, the price ladder, the save badge, the
  charge-restating CTA — is the same everywhere by construction.

  Terms are DATA-DRIVEN from the real Subscription::Plan records (personalPlans
  prop → pricingTerms). Monthly is never rendered even if the API sends it.
  Change a price in the backend and every surface follows with no deploy.
-->
<template>
  <div
    class="w-full max-w-[560px] overflow-hidden rounded-[22px] bg-white text-left shadow-[0px_2px_4px_rgba(0,0,0,0.06),0px_14px_26px_-10px_rgba(15,23,42,0.16),0px_28px_40px_-20px_rgba(15,23,42,0.20)]"
  >
    <div class="m-2 rounded-[16px] bg-gradient-to-b from-secondary-100/70 to-secondary-50/40 p-7 sm:p-8">
      <h2 v-if="heading && terms.length > 1" class="text-[21px] font-bold tracking-tight text-neutral-900">
        {{ heading }}
      </h2>

      <div class="space-y-2.5" :class="heading && terms.length > 1 ? 'mt-6' : ''" role="radiogroup" aria-label="Billing term">
        <button
          v-for="t in terms"
          :key="t.id"
          type="button"
          role="radio"
          :aria-checked="term === t.id"
          class="flex w-full items-center gap-3.5 rounded-2xl bg-white px-4 py-3.5 text-left ring-1 transition-all sm:px-5"
          :class="
            term === t.id
              ? 'ring-2 ring-secondary-500 shadow-[0_8px_20px_-10px_rgba(20,16,40,0.25)]'
              : 'ring-neutral-200 hover:ring-neutral-300'
          "
          @click="term = t.id"
        >
          <span
            class="relative size-5 shrink-0 rounded-full border-2 transition-colors"
            :class="term === t.id ? 'border-secondary-600' : 'border-neutral-300'"
          >
            <span v-if="term === t.id" class="absolute inset-[3px] rounded-full bg-secondary-600" />
          </span>

          <span class="min-w-0 flex-1">
            <span class="flex flex-wrap items-center gap-2">
              <span class="text-[15px] font-bold text-neutral-900">{{ t.termLabel }}</span>
              <span
                v-if="t.savePct > 0"
                class="rounded-full bg-success-50 px-2 py-0.5 text-[10.5px] font-bold text-success-700"
              >
                Save {{ t.savePct }}%
              </span>
            </span>
            <span class="mt-0.5 block text-[12.5px] text-neutral-500">
              {{ t.cycle }} ·
              <DiscountedPrice
                :original="t.total"
                :discounted="termDiscount(t)?.total"
                :note="termNote(t)"
              />
            </span>
          </span>

          <!-- The selected row's price reads larger: keeps the at-a-glance
               ladder without losing the anchor. -->
          <span class="shrink-0 text-right">
            <span
              v-if="termDiscount(t)"
              class="block text-[12px] leading-none text-neutral-400 line-through"
            >{{ formatCurrency(t.perMonth) }}</span>
            <span
              class="block font-bold leading-none tracking-tight transition-all"
              :class="[
                term === t.id ? 'text-[28px] text-neutral-900' : 'text-[19px] text-neutral-500',
                termDiscount(t) ? 'mt-1' : '',
              ]"
            >{{ formatCurrency(termDiscount(t)?.perMonth ?? t.perMonth) }}</span>
            <span class="mt-0.5 block text-[11.5px] text-neutral-400">/month</span>
          </span>
        </button>
      </div>

      <!-- Signup surfaces link straight out; the paywall emits so it can open a
           Stripe checkout session instead. -->
      <a
        v-if="signupPath"
        :href="`${signupPath}?plan=${term}`"
        class="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-secondary-600 px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-secondary-700"
      >
        {{ activeTerm.cta }}
        <ArrowRightIcon class="size-4" />
      </a>
      <button
        v-else
        type="button"
        :disabled="isPending || !activeTerm.planId"
        class="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-secondary-600 px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-secondary-700 disabled:cursor-not-allowed disabled:opacity-60"
        @click="emit('purchase', activeTerm.planId)"
      >
        {{ isPending ? 'Starting…' : purchaseLabel || activeTerm.cta }}
        <ArrowRightIcon v-if="!isPending" class="size-4" />
      </button>

      <p class="mt-3.5 text-center text-[12.5px] text-neutral-500">{{ fineprint }}</p>

      <ul v-if="checklist?.length" class="mt-6 flex flex-col gap-y-3 border-t border-neutral-100 pt-6">
        <li
          v-for="item in checklist"
          :key="item"
          class="flex items-center gap-3 text-[14px] text-neutral-800"
        >
          <span class="grid size-5 shrink-0 place-items-center rounded-full bg-success-100 text-success-700">
            <CheckIcon class="size-3" stroke-width="3" />
          </span>
          {{ item }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import ArrowRightIcon from '~icons/lucide/arrow-right'
import CheckIcon from '~icons/lucide/check'
import { pricingTerms, type PersonalPlanProp, type PricingTerm } from '@/modules/site/utils/personalPlans'
import { formatCurrency } from '@/shared/utils/formatCurrency.js'
import { useOfferCountdown, offerCoversPlan, type LimitedTimeOffer } from '@/shared/composables/useOfferCountdown'
import { couponWording } from '@/shared/composables/useCouponCode'
import DiscountedPrice from '@/shared/components/DiscountedPrice.vue'

const props = withDefaults(
  defineProps<{
    personalPlans?: PersonalPlanProp[]
    /** Which row is preselected. Quarterly everywhere (Foti, 2026-07-13). */
    defaultTerm?: PricingTerm['id']
    /** Card heading. Null on the homepage, where the section h2 already says it. */
    heading?: string | null
    fineprint?: string
    /** Optional feature list under a divider. Homepage only. */
    checklist?: string[]
    /** When set, the CTA is a link to signup with the chosen term. When absent,
     *  the CTA is a button that emits `purchase` with the plan id (the paywall). */
    signupPath?: string | null
    isPending?: boolean
    /** A shorter button label for a compact surface, such as the plan modal
     *  on a Roadmap. */
    purchaseLabel?: string | null
  }>(),
  {
    personalPlans: undefined,
    defaultTerm: 'quarterly',
    heading: 'Choose your billing term',
    fineprint: "If your first call isn't worth it, full refund within 14 days. Cancel anytime.",
    checklist: undefined,
    signupPath: null,
    isPending: false,
    purchaseLabel: null,
  },
)

const emit = defineEmits<{ purchase: [planId: number] }>()

const terms = computed(() => pricingTerms(props.personalPlans))
// One plan: the yearly term is the only one, whatever the default.
const term = ref<PricingTerm['id']>(
  terms.value.some((t) => t.id === props.defaultTerm) ? props.defaultTerm : terms.value[0].id,
)

// --- Limited-time offer (shared prop; members/mentors never receive it). ---
// The countdown gates the display, so expiry drops the struck prices here in
// the same tick as the bar. Fallback terms (planId 0) never discount: the
// figures wouldn't be tied to real plan records.
const page = usePage()
const offer = computed<LimitedTimeOffer | null>(
  () => (page.props.shared as { offer?: LimitedTimeOffer | null } | undefined)?.offer ?? null,
)
const { expired: offerExpired } = useOfferCountdown(() => offer.value?.endsAt)
const activeOffer = computed(() => (offer.value && !offerExpired.value ? offer.value : null))

const round2 = (amount: number) => Math.round(amount * 100) / 100

const termDiscount = (t: PricingTerm) => {
  if (!activeOffer.value || t.planId === 0 || !offerCoversPlan(activeOffer.value, t.planId)) return undefined
  const pct = activeOffer.value.percentOff
  return { total: round2(t.total * (1 - pct / 100)), perMonth: round2(t.perMonth * (1 - pct / 100)) }
}

const termNote = (t: PricingTerm) => {
  if (!termDiscount(t) || !activeOffer.value) return 'total'
  const interval = t.id === 'annual' ? 'annually' : 'quarterly'
  return couponWording(activeOffer.value, interval) || 'total'
}

const activeTerm = computed(() => {
  const t = terms.value.find((x) => x.id === term.value) ?? terms.value[0]
  const total = termDiscount(t)?.total ?? t.total
  return { ...t, cta: `Start · ${formatCurrency(total)} ${t.billedLabel}` }
})
</script>
